import { ReadonlySignal, Signal, signal } from "@preact/signals";

const MAX_TIMEOUT_MS = 2147483647; // max value for the delay in setTimeout

const UNITS = {
    year: 365 * 24 * 60 * 60 * 1000,
    get month() {
        const now = new Date();
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

        return nextMonth.getTime() - now.getTime();
    },
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000,
} as const;

const getMsUntilEnd = (unit: keyof typeof UNITS) => {
    const now = new Date();
    const end = new Date(now);

    if (unit === "year") {
        end.setFullYear(now.getFullYear() + 1, 0, 1);
        end.setHours(0, 0, 0, 0);
    } else if (unit === "month") {
        end.setMonth(now.getMonth() + 1, 1);
        end.setHours(0, 0, 0, 0);
    } else if (unit === "day") {
        end.setDate(now.getDate() + 1);
        end.setHours(0, 0, 0, 0);
    } else if (unit === "minute") {
        end.setMinutes(now.getMinutes() + 1, 0, 0);
    } else if (unit === "second") {
        end.setSeconds(now.getSeconds() + 1, 0);
    }

    return end.getTime() - now.getTime();
};

const setLongTimeout = (callback: () => void, delay: number) => {
    if (delay <= MAX_TIMEOUT_MS) {
        return { id: window.setTimeout(callback, delay) };
    }

    const ref = { id: 0 };

    ref.id = window.setTimeout(() => {
        const remaining = delay - MAX_TIMEOUT_MS;
        const next = setLongTimeout(callback, remaining);

        ref.id = next.id;
    });

    return ref;
};

const clearLongTimeout = (ref: { id: number }) => {
    window.clearTimeout(ref.id);
};

const STUB_REF = { id: 0 };

export type AppTimeUnit = keyof typeof UNITS;

export default class AppTime {
    private static listenersCount = {
        year: 0,
        month: 0,
        day: 0,
        hour: 0,
        minute: 0,
        second: 0,
    };
    private static extListeners: Record<AppTimeUnit, ((d: Date) => void)[]> = {
        year: [],
        month: [],
        day: [],
        hour: [],
        minute: [],
        second: [],
    };
    private static cleanupRefs = {
        year: STUB_REF,
        month: STUB_REF,
        day: STUB_REF,
        hour: STUB_REF,
        minute: STUB_REF,
        second: STUB_REF,
    };

    public static readonly signals = {
        year: signal(new Date()) as ReadonlySignal<Date>,
        month: signal(new Date()) as ReadonlySignal<Date>,
        day: signal(new Date()) as ReadonlySignal<Date>,
        hour: signal(new Date()) as ReadonlySignal<Date>,
        minute: signal(new Date()) as ReadonlySignal<Date>,
        second: signal(new Date()) as ReadonlySignal<Date>,
    } as const;

    public static readonly track = (unit: keyof typeof UNITS) => {
        if (AppTime.listenersCount[unit] === 0) {
            const untilEnd = getMsUntilEnd(unit);

            const tick = () => {
                const now = new Date();
                const signal = AppTime.signals[unit] as Signal<Date>;
                const duration = UNITS[unit];

                signal.value = new Date();
                AppTime.cleanupRefs[unit] = setLongTimeout(tick, duration);
                AppTime.extListeners[unit].forEach((listener) => listener(now));
            };

            AppTime.cleanupRefs[unit] = setLongTimeout(tick, untilEnd);
        }

        AppTime.listenersCount[unit] += 1;

        return AppTime.signals[unit];
    };

    public static readonly untrack = (unit: keyof typeof UNITS) => {
        AppTime.listenersCount[unit] -= 1;

        if (AppTime.listenersCount[unit] === 0) {
            clearLongTimeout(AppTime.cleanupRefs[unit]);
            AppTime.cleanupRefs[unit] = STUB_REF;
        }
    };

    public static readonly on = (
        unit: keyof typeof UNITS,
        listener: (date: Date) => void,
    ) => {
        AppTime.extListeners[unit].push(listener);
        AppTime.track(unit);

        return () => {
            AppTime.extListeners[unit] = AppTime.extListeners[unit].filter(
                (item) => item !== listener,
            );
            AppTime.untrack(unit);
        };
    };
}
